/**
 * SEO için Structured Data (JSON-LD) şemalarını oluşturan yardımcı fonksiyonlar
 * https://schema.org/ şemalarına göre hazırlanmıştır
 */

import { BRAND_KNOWN_AS, BRAND_LEGAL_NAME } from "@/lib/brand";
import { SITE_URL } from "@/lib/site";
import { toAbsoluteUrl } from "@/lib/seo/urls";

// Ürün şeması — fiyat teklifi ile satış: Offer’da geçersiz boş price kullanılmaz
export function generateProductSchema(product: {
  name: string;
  description: string;
  images: string[];
  category: string;
  url: string;
}) {
  const raw =
    product.images.length > 0
      ? product.images
      : [`${SITE_URL}/images/products-og.webp`];
  const images = raw.map((src) => toAbsoluteUrl(src));

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.description,
    image: images.length === 1 ? images[0] : images,
    category: product.category,
    sku: product.name.replace(/\s+/g, '-').toUpperCase().substring(0, 15),

    brand: {
      "@type": "Brand",
      name: BRAND_KNOWN_AS,
    },
  };
}

// Genel Web Sitesi Şeması (Google'da arama kutusu ve site otoritesi için)
export function generateWebSiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: BRAND_LEGAL_NAME,
    alternateName: [BRAND_KNOWN_AS, "Filiz Kozmetik"],
    url: SITE_URL,
    description: "Türkiye'nin lider berber ve kuaför salon mobilyaları üreticisi. Özel üretim berber tezgahları ve kuaför koltukları.",
    potentialAction: [
      {
        "@type": "SearchAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate: `${SITE_URL}/urunler?search={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      }
    ]
  };
}

// Yerel işletme şeması oluşturma
export function generateLocalBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": SITE_URL,
    name: BRAND_LEGAL_NAME,
    alternateName: [BRAND_KNOWN_AS],
    description: "Türkiye'nin lider berber tezgahları, berber koltukları, kuaför tezgahları ve kuaför koltukları üreticisi. Özel üretim ve garantili salon mobilyaları.",
    image: `${SITE_URL}/images/logo.webp`,
    url: SITE_URL,
    telephone: "+90 532 345 7227",
    priceRange: "₺₺",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Piyalepaşa Mh. Baruthane Sk. No:23",
      addressLocality: "Beyoğlu",
      addressRegion: "İstanbul",
      postalCode: "34440",
      addressCountry: "TR",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 41.0438,
      longitude: 28.9793,
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "09:00",
        closes: "18:00",
      },
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: "Saturday",
        opens: "09:00",
        closes: "14:00",
      },
    ],
    sameAs: [
      "https://filizkozmetik.net",
      "https://www.facebook.com/filizdekorasyon",
      "https://www.instagram.com/filizdekorasyonkozmetik.tr/",
    ],
  };
}

// Breadcrumb şeması oluşturma
export function generateBreadcrumbSchema(
  breadcrumbItems: { name: string; url: string }[]
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbItems.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

// Organizasyon şeması
export function generateOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: BRAND_LEGAL_NAME,
    alternateName: [BRAND_KNOWN_AS],
    description: "Türkiye'nin lider berber tezgahları, berber koltukları, kuaför tezgahları ve kuaför koltukları üreticisi. Özel üretim ve garantili salon mobilyaları.",
    url: SITE_URL,
    logo: `${SITE_URL}/images/logo.webp`,
    sameAs: [
      "https://filizkozmetik.net",
      "https://www.facebook.com/filizdekorasyon",
      "https://www.instagram.com/filizdekorasyonkozmetik.tr/",
    ],
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: "+90 532 345 7227",
        contactType: "customer service",
        availableLanguage: ["Turkish"],
      },
    ],
    address: {
      "@type": "PostalAddress",
      streetAddress: "Piyalepaşa Mh. Baruthane Sk. No:23",
      addressLocality: "Beyoğlu",
      addressRegion: "İstanbul",
      postalCode: "34440",
      addressCountry: "TR",
    },
  };
}

export function generateFaqSchema(
  questions: { question: string; answer: string }[]
) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: questions.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

export function generateItemListSchema(input: {
  name: string;
  url: string;
  items: { name: string; url: string }[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: input.name,
    url: input.url,
    itemListElement: input.items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: item.url,
      name: item.name,
    })),
  };
}
